// JSON-LD schema generators — optimized for rich results in Google SERP
// Main targets: "reggaeton bruxelles", "reggaeton brussels", "reggaeton bruselas"

const BASE_URL = 'https://www.invasionlatina.be';

export const createOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${BASE_URL}/#organization`,
  "name": "Invasion Latina",
  "alternateName": [
    "Invasion Latina Brussels",
    "Invasion Latina Bruselas",
    "Invasion Latina Brussel",
    "Fiesta Latina Bruselas",
    "Fiesta Reggaeton Bruselas"
  ],
  "url": BASE_URL,
  "logo": `${BASE_URL}/images/logo.png`,
  "image": `${BASE_URL}/images/logo.png`,
  "description": "La plus grande soirée reggaeton et latino de Bruxelles depuis 2009 · La fiesta latina más grande de Bélgica desde 2009 · Events mensuels au Mirano Continental + Open Air annuel Place du Congrès. Reggaeton, latin trap, dembow, bachata. Bruxelles · Brussels · Bruselas.",
  "foundingDate": "2009-05",
  "founder": {
    "@type": "Person",
    "name": "Invasion Latina Team"
  },
  "slogan": "It's not a festival. It's our summer.",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Bruxelles",
    "addressRegion": "Bruxelles-Capitale",
    "addressCountry": "BE",
    "postalCode": "1000"
  },
  "areaServed": [
    { "@type": "City", "name": "Bruxelles" },
    { "@type": "City", "name": "Brussels" },
    { "@type": "City", "name": "Bruselas" },
    { "@type": "City", "name": "Brussel" },
    { "@type": "Country", "name": "Belgique" }
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "email": "info@invasionlatina.be",
    "telephone": "+32478814497",
    "contactType": "customer service",
    "availableLanguage": ["French", "Spanish", "English", "Dutch"]
  },
  "sameAs": [
    "https://www.instagram.com/invasionlatina",
    "https://www.facebook.com/invasionlatina",
    "https://www.tiktok.com/@invasionlatina",
    "https://www.youtube.com/@invasionlatina"
  ]
});

// MusicEvent — more specific than Event for reggaeton nights, triggers music-specific rich results
export const createEventSchema = (event) => {
  const type = event.type === 'openair' ? 'Festival' : 'MusicEvent';
  const [startTime, endTime] = (event.time || '22:00 - 04:00').split(' - ');
  const isSoldOut = event.status === 'sold_out';

  return {
    "@context": "https://schema.org",
    "@type": type,
    "@id": `${BASE_URL}/agenda#${event.id}`,
    "name": event.title,
    "alternateName": [`${event.title} Bruxelles`, `${event.title} Brussels`],
    "description": event.subtitle_fr || event.subtitle || `${event.title} — Soirée reggaeton, latin trap, bachata et musique urbaine latine à ${event.city}. Invasion Latina.`,
    "image": event.poster_url ? (event.poster_url.startsWith('http') ? event.poster_url : `${BASE_URL}${event.poster_url}`) : `${BASE_URL}/images/og-image-default.jpg`,
    "startDate": `${event.date}T${(startTime || '22:00').trim()}:00+02:00`,
    "endDate": `${event.date}T${(endTime || '04:00').trim()}:00+02:00`,
    "eventStatus": isSoldOut ? "https://schema.org/EventSoldOut" : "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "genre": ["Reggaeton", "Latin Trap", "Dembow", "Bachata", "Afrobeats", "Dancehall"],
    "inLanguage": ["fr", "nl", "en", "es"],
    "location": {
      "@type": "MusicVenue",
      "name": event.venue || (event.type === 'openair' ? 'Place du Congrès' : 'Mirano Continental'),
      "address": {
        "@type": "PostalAddress",
        "streetAddress": event.address || (event.type === 'openair' ? 'Place du Congrès' : 'Chaussée de Louvain 38'),
        "addressLocality": event.city || 'Bruxelles',
        "postalCode": "1000",
        "addressCountry": "BE"
      }
    },
    "organizer": {
      "@type": "Organization",
      "name": "Invasion Latina",
      "url": BASE_URL,
      "sameAs": [
        "https://www.instagram.com/invasionlatina",
        "https://www.facebook.com/invasionlatina"
      ]
    },
    "performer": [
      {
        "@type": "PerformingGroup",
        "name": "Invasion Latina DJs",
        "genre": ["Reggaeton", "Latin Trap", "Dembow"]
      }
    ],
    "offers": isSoldOut ? {
      "@type": "Offer",
      "url": event.xceed_url || `${BASE_URL}/booking`,
      "availability": "https://schema.org/SoldOut"
    } : {
      "@type": "Offer",
      "url": event.xceed_url || `${BASE_URL}/booking`,
      "price": event.type === 'openair' ? "20" : "12",
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "validFrom": new Date().toISOString()
    },
    "typicalAgeRange": "18-",
    "isAccessibleForFree": false,
    "audience": {
      "@type": "Audience",
      "audienceType": "Adults"
    }
  };
};

export const createFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const createBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `${BASE_URL}${item.path}`
  }))
});

export const createVideoObjectSchema = (video) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  "name": video.title,
  "description": `${video.title} — Invasion Latina ${video.category === 'openair' ? 'Open Air Bruxelles' : 'Mirano Continental'}. Soirée reggaeton et latino à Bruxelles.`,
  "thumbnailUrl": video.thumbnail?.startsWith('http') ? video.thumbnail : `${BASE_URL}${video.thumbnail}`,
  "uploadDate": video.upload_date || new Date().toISOString().split('T')[0],
  "contentUrl": video.youtube_url,
  "embedUrl": video.youtube_url?.replace('watch?v=', 'embed/'),
  "publisher": {
    "@type": "Organization",
    "name": "Invasion Latina",
    "logo": {
      "@type": "ImageObject",
      "url": `${BASE_URL}/images/logo.png`
    }
  }
});

// WebSite schema with sitelinks search box — helps Google show a search box below Invasion Latina in results
export const createWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${BASE_URL}/#website`,
  "url": BASE_URL,
  "name": "Invasion Latina",
  "alternateName": ["Fiesta Latina Bruselas", "Reggaeton Bruxelles"],
  "description": "Reggaeton Bruxelles · Bruselas · Brussels — Invasion Latina, la plus grande soirée latino et fiesta reggaeton de Belgique depuis 2009.",
  "publisher": { "@id": `${BASE_URL}/#organization` },
  "inLanguage": ["fr-BE", "nl-BE", "en", "es-ES"]
});

// FAQs: optimized with target keywords in the questions themselves
export const DEFAULT_FAQ_SCHEMA = createFAQSchema([
  {
    question: "Où se passe la meilleure soirée reggaeton à Bruxelles ?",
    answer: "Invasion Latina est LA référence reggaeton et latino à Bruxelles depuis 2009. Events mensuels au Mirano Continental (Chaussée de Louvain 38, 1210 Saint-Josse-ten-Noode) et Open Air annuel à la Place du Congrès, 1000 Bruxelles."
  },
  {
    question: "Quelle est la prochaine soirée reggaeton à Bruxelles ?",
    answer: "Consultez notre agenda sur invasionlatina.be/agenda pour voir toutes les prochaines soirées reggaeton, latin trap et bachata à Bruxelles. L'Invasion Latina Open Air 2026 a lieu le samedi 11 juillet 2026 de 14h00 à 22h00 à la Place du Congrès."
  },
  {
    question: "Comment acheter des tickets pour Invasion Latina Open Air 2026 ?",
    answer: "Les tickets sont disponibles sur invasionlatina.be via Xceed. Plusieurs vagues : Early Birds, Wave 1, Wave 2, Wave 3, VIP. Plus tu achètes tôt, moins c'est cher. Open Air 2026 le 11 juillet, Place du Congrès, Bruxelles."
  },
  {
    question: "Quelle musique est jouée à Invasion Latina ?",
    answer: "Reggaeton, Latin Trap, Dembow, Bachata, Afrobeats, Dancehall, et toute la musique urbaine latine. C'est LA référence reggaeton à Bruxelles et la plus grande soirée latino de Belgique."
  },
  {
    question: "Quel âge minimum pour entrer à Invasion Latina ?",
    answer: "L'accès est réservé aux 18 ans et plus. Une pièce d'identité valide est obligatoire à l'entrée. Safe space — zéro tolérance pour le harcèlement."
  },
  {
    question: "Reggaeton Brussels — where is the best party?",
    answer: "Invasion Latina is the biggest reggaeton and latin night in Brussels since 2009. Monthly events at Mirano Continental + annual Open Air at Place du Congrès. Over 240,000 tickets sold. Info: invasionlatina.be"
  },
  {
    question: "¿Dónde está la mejor fiesta reggaeton en Bruselas?",
    answer: "Invasion Latina es LA fiesta reggaeton y latina más grande de Bruselas desde 2009. Eventos mensuales en el Mirano Continental y Open Air anual en Place du Congrès. Tickets y agenda: invasionlatina.be"
  },
  {
    question: "¿Cuándo es el próximo evento Invasion Latina en Bruselas?",
    answer: "El próximo gran evento es el Invasion Latina Open Air 2026, sábado 11 de julio de 2026 de 14:00 a 22:00 en Place du Congrès, 1000 Bruselas. Además hay eventos mensuales en el Mirano Continental. Agenda completa: invasionlatina.be/agenda"
  },
  {
    question: "¿Dónde comprar tickets para Invasion Latina Open Air 2026?",
    answer: "Los tickets están disponibles en invasionlatina.be a través de Xceed. Hay varias olas: Early Birds, Wave 1, Wave 2, Wave 3 y VIP. Cuanto antes compres, menos cuesta. Open Air 2026: 11 de julio, Place du Congrès, Bruselas."
  },
  {
    question: "¿Qué música ponen en Invasion Latina Bruselas?",
    answer: "Reggaeton, Latin Trap, Dembow, Bachata, Afrobeats, Dancehall, y toda la música urbana latina. Es LA referencia reggaeton en Bruselas y la fiesta latina más grande de Bélgica desde hace 16 años."
  }
]);
